/**
 * The staff notice: Part A (information on the backoffice's processing) and
 * Part B (acknowledgment and confidentiality), as the gdpr-officer drafted
 * them. A fact the owner has yet to supply reads "Angaben folgen" /
 * "details to follow"; STAFF_NOTICE_VERSION in @revia/types names this
 * text's version.
 */
export type StaffNoticeBlock =
	| { readonly lead?: string; readonly text: string }
	| { readonly items: readonly string[] };

export type StaffNoticeText = {
	readonly title: string;
	readonly version: string;
	readonly partA: {
		readonly title: string;
		readonly sections: readonly {
			readonly heading: string;
			readonly blocks: readonly StaffNoticeBlock[];
		}[];
	};
	readonly partB: {
		readonly title: string;
		readonly intro: string;
		readonly items: readonly string[];
	};
	readonly checkbox: string;
};

export const de: StaffNoticeText = {
	"title": "Hinweise für das Revia-Team zum Backoffice",
	"version": "Fassung: Angaben folgen",
	"partA": {
		"title": "Teil A — Wie wir Ihre Daten im Backoffice verarbeiten",
		"sections": [
			{
				"heading": "1. Verantwortlicher",
				"blocks": [
					{
						"text": "ReVia GmbH, Reichenberger Straße 107, 10999 Berlin, E-Mail: julian@revia.to. Datenschutzbeauftragter: Angaben folgen."
					}
				]
			},
			{
				"heading": "2. Was wir verarbeiten und wozu",
				"blocks": [
					{
						"lead": "Anmeldung.",
						"text": "Sie melden sich mit Ihrer E-Mail-Adresse an. Nur wenn die Adresse zum Team von Revia gehört, senden wir einen sechsstelligen Code an sie. Er gilt 10 Minuten, wird nur verschlüsselt gespeichert und mit der Verwendung oder nach Ablauf gelöscht. Wir begrenzen die Zahl der Codes je Adresse und Zeitraum zum Schutz vor Missbrauch. Ihr Browser speichert eine Sitzung, damit Sie angemeldet bleiben."
					},
					{
						"lead": "Konto und Rolle.",
						"text": "Wir führen ein Konto mit interner Kennung, E-Mail-Adresse, Namen, Sprache und Zeitpunkten der Erstellung und Änderung sowie Ihre Rolle im Team von Revia, die festlegt, was Sie im Backoffice sehen und tun dürfen."
					},
					{
						"lead": "Nachweis dieser Hinweise.",
						"text": "Wir speichern, welche Fassung dieser Hinweise Ihnen angezeigt wurde und wann Sie Teil B bestätigt haben, zusammen mit der Kennung Ihres Browsers."
					},
					{
						"lead": "Zuordnung Ihrer Handlungen.",
						"text": "Geben Sie eine Zugangsanfrage frei oder stellen Sie eine Lizenz aus, speichern wir Ihre Kennung und den Zeitpunkt an diesem Datensatz. Damit ist nachvollziehbar, wer einen Gründer zugelassen oder eine Lizenz signiert hat."
					},
					{
						"lead": "Sicherheit.",
						"text": "Unsere Server protokollieren zu jeder Anfrage technische Einträge wie IP-Adresse, Zeitpunkt, aufgerufene Funktion und Ergebnis."
					}
				]
			},
			{
				"heading": "3. Rechtsgrundlagen",
				"blocks": [
					{
						"items": [
							"Anmeldung, Konto und Rolle: Art. 6 Abs. 1 lit. b DSGVO, weil Sie das Backoffice zur Erfüllung Ihres Beschäftigungs- oder Dienstverhältnisses (Angaben folgen) nutzen.",
							"Zuordnung Ihrer Handlungen, Nachweis dieser Hinweise und Sicherheit: Art. 6 Abs. 1 lit. f DSGVO. Unser Interesse ist, nachweisen zu können, wer über Zugänge und Lizenzen entschieden hat, und das Backoffice gegen unbefugten Zugriff zu schützen (Art. 5 Abs. 2, Art. 32 DSGVO).",
							"Speichern und Auslesen auf Ihrem Gerät für Sitzung und Schutz: unbedingt erforderlich für das Backoffice, das Sie nutzen (§ 25 Abs. 2 Nr. 2 TDDDG)."
						]
					},
					{
						"text": "Wir stützen uns nicht auf eine Einwilligung. Sie müssen der Verarbeitung also nicht zustimmen; sie folgt aus Ihrer Tätigkeit."
					}
				]
			},
			{
				"heading": "4. Empfänger und Ort",
				"blocks": [
					{
						"text": "Google Cloud EMEA Limited / Google Ireland Limited mit Google LLC als Unterauftragsverarbeiter (Hosting, Datenbank, Serverfunktionen, Anmeldung, Protokolle); E-Mail-Versanddienst für Anmeldecodes: Angaben folgen. Datenbank: Angaben folgen. Zugriffe von Google LLC aus den USA beruhen auf dem EU-US Data Privacy Framework (Art. 45 DSGVO) und ergänzend auf Standardvertragsklauseln (Art. 46 Abs. 2 lit. c DSGVO)."
					},
					{
						"text": "Andere Mitglieder des Teams von Revia mit Zugriff auf das Backoffice sehen, wer eine Anfrage freigegeben oder eine Lizenz ausgestellt hat."
					}
				]
			},
			{
				"heading": "5. Speicherdauer",
				"blocks": [
					{
						"items": [
							"Konto und Rolle: für die Dauer Ihrer Tätigkeit. Mit dem Ende entziehen wir die Rolle sofort und löschen das Konto (Frist: Angaben folgen).",
							"Nachweis dieser Hinweise: bis zum Ende des dritten Kalenderjahres nach Ihrem Ausscheiden.",
							"Zuordnung Ihrer Handlungen: solange der freigegebene oder signierte Datensatz besteht (siehe Datenschutzerklärung von Revia).",
							"Serverprotokolle: Angaben folgen."
						]
					}
				]
			},
			{
				"heading": "6. Ihre Rechte",
				"blocks": [
					{
						"text": "Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung und Datenübertragbarkeit (Art. 15 bis 18, 20 DSGVO) und das Recht, einer Verarbeitung auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO aus Gründen Ihrer besonderen Situation zu widersprechen (Art. 21 DSGVO). Schreiben Sie an die Datenschutz-Kontaktadresse (Angaben folgen). Sie können sich bei einer Aufsichtsbehörde beschweren; zuständige Aufsichtsbehörde: Angaben folgen."
					},
					{
						"text": "Die Angabe Ihrer E-Mail-Adresse ist für die Anmeldung erforderlich. Ohne sie können Sie das Backoffice nicht nutzen. Es gibt keine automatisierten Entscheidungen im Sinne von Art. 22 DSGVO."
					}
				]
			}
		]
	},
	"partB": {
		"title": "Teil B — Kenntnisnahme und Vertraulichkeit",
		"intro": "Im Backoffice sehen Sie personenbezogene Daten anderer: Namen, gekürzte E-Mail-Adressen und Nachrichten von Personen, die Zugang anfragen, sowie Angaben zu Kunden und ihren Installationen. Dafür gilt:",
		"items": [
			"Sie verarbeiten diese Daten nur, soweit Ihre Aufgabe es verlangt, und nur nach den Vorgaben von Revia (Art. 29 DSGVO).",
			"Sie behandeln sie vertraulich, auch nach dem Ende Ihrer Tätigkeit. Sie geben sie nicht an Unbefugte weiter, kopieren sie nicht aus dem Backoffice heraus und versuchen nicht, aus gekürzten oder verschlüsselten Angaben die vollständige E-Mail-Adresse zu ermitteln.",
			"Sie geben eine Anfrage nur frei oder stellen eine Lizenz nur aus, wenn Revia es so vorsieht; jede dieser Handlungen wird Ihnen zugeordnet.",
			"Sie melden sich nur selbst an, geben keinen Code weiter und melden einen Verdacht auf unbefugten Zugriff oder eine Datenpanne unverzüglich an die interne Meldeadresse (Angaben folgen).",
			"Verstöße können arbeits- oder dienstrechtliche Folgen haben und nach § 42 BDSG strafbar sein."
		]
	},
	"checkbox": "Ich habe die Hinweise zur Datenverarbeitung im Backoffice (Fassung: Angaben folgen) gelesen und verpflichte mich zur Vertraulichkeit nach Teil B."
};

export const en: StaffNoticeText = {
	"title": "Notice for the Revia team on the backoffice",
	"version": "Version: details to follow",
	"partA": {
		"title": "Part A — How we process your data in the backoffice",
		"sections": [
			{
				"heading": "1. Controller",
				"blocks": [
					{
						"text": "ReVia GmbH, Reichenberger Straße 107, 10999 Berlin, Germany, email: julian@revia.to. Data protection officer: details to follow."
					}
				]
			},
			{
				"heading": "2. What we process and why",
				"blocks": [
					{
						"lead": "Sign-in.",
						"text": "You sign in with your email address. Only if the address belongs to Revia's team do we send a six-digit code to it. It is valid for 10 minutes, stored only in hashed form, and deleted when used or once expired. We limit the number of codes per address and period to prevent abuse. Your browser stores a session so you stay signed in."
					},
					{
						"lead": "Account and role.",
						"text": "We keep an account with an internal identifier, email address, name, language and the times it was created and changed, and your role in Revia's team, which sets what you may see and do in the backoffice."
					},
					{
						"lead": "Record of this notice.",
						"text": "We store which version of this notice you were shown and when you confirmed Part B, with your browser's identification string."
					},
					{
						"lead": "Attribution of your actions.",
						"text": "When you approve an access request or issue a licence, we store your identifier and the time on that record. This shows who admitted a founder or signed a licence."
					},
					{
						"lead": "Security.",
						"text": "Our servers log technical entries for each request, such as IP address, time, function called and result."
					}
				]
			},
			{
				"heading": "3. Legal bases",
				"blocks": [
					{
						"items": [
							"Sign-in, account and role: Art. 6(1)(b) GDPR, because you use the backoffice to perform your employment or service contract (details to follow).",
							"Attribution of your actions, record of this notice and security: Art. 6(1)(f) GDPR. Our interest is being able to show who decided on access and licences, and protecting the backoffice against unauthorised access (Art. 5(2), Art. 32 GDPR).",
							"Storing and reading on your device for the session and protection: strictly necessary for the backoffice you use (§ 25(2) No. 2 TDDDG)."
						]
					},
					{
						"text": "We do not rely on consent. You are not asked to agree to this processing; it follows from your work."
					}
				]
			},
			{
				"heading": "4. Recipients and location",
				"blocks": [
					{
						"text": "Google Cloud EMEA Limited / Google Ireland Limited with Google LLC as sub-processor (hosting, database, server functions, sign-in, logs); email delivery service for sign-in codes: details to follow. Database: details to follow. Access by Google LLC from the USA relies on the EU-US Data Privacy Framework (Art. 45 GDPR) and additionally on standard contractual clauses (Art. 46(2)(c) GDPR)."
					},
					{
						"text": "Other members of Revia's team with backoffice access see who approved a request or issued a licence."
					}
				]
			},
			{
				"heading": "5. Retention",
				"blocks": [
					{
						"items": [
							"Account and role: for the duration of your work. When it ends we withdraw the role at once and delete the account (period: details to follow).",
							"Record of this notice: until the end of the third calendar year after you leave.",
							"Attribution of your actions: as long as the approved or signed record exists (see Revia's privacy notice).",
							"Server logs: details to follow."
						]
					}
				]
			},
			{
				"heading": "6. Your rights",
				"blocks": [
					{
						"text": "You have the right of access, rectification, erasure, restriction and data portability (Arts. 15 to 18, 20 GDPR), and the right to object, on grounds relating to your particular situation, to processing based on Art. 6(1)(f) GDPR (Art. 21 GDPR). Write to our privacy contact address (details to follow). You may complain to a supervisory authority; ours: details to follow."
					},
					{
						"text": "Your email address is required to sign in. Without it you cannot use the backoffice. There are no automated decisions within the meaning of Art. 22 GDPR."
					}
				]
			}
		]
	},
	"partB": {
		"title": "Part B — Acknowledgment and confidentiality",
		"intro": "In the backoffice you see other people's personal data: names, shortened email addresses and messages of people requesting access, and details of clients and their installations. The following applies:",
		"items": [
			"You process this data only as far as your task requires, and only on Revia's instructions (Art. 29 GDPR).",
			"You keep it confidential, also after your work ends. You do not pass it to unauthorised persons, do not copy it out of the backoffice, and do not try to recover a full email address from shortened or hashed values.",
			"You approve a request or issue a licence only as Revia provides; each such action is attributed to you.",
			"You sign in only as yourself, never share a code, and report any suspected unauthorised access or data breach without delay to the internal reporting address (details to follow).",
			"Breaches can have consequences under your employment or service relationship and can be a criminal offence under § 42 BDSG."
		]
	},
	"checkbox": "I have read the notice on data processing in the backoffice (version: details to follow) and undertake to keep confidentiality under Part B."
};

